import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { UNSAFE_NavigationContext as NavigationContext } from "react-router-dom";
import { Download, Plus, UserRoundSearch, UsersRound } from "lucide-react";
import {
  useDomainRouterMutation,
  useDomainRouterQuery,
} from "../../../state/domainRouterHooks";
import { readDomainRouterErrorMessage } from "../../../state/domainRouterError";
import { HeaderShell } from "../components/HeaderShell";
import {
  StudentsFamiliesFilters,
  type FilterValue,
} from "../components/StudentsFamiliesFilters";
import { StudentsFamiliesStatusBadge } from "../components/StudentsFamiliesStatusBadge";
import {
  ActionIconButton,
  SearchFilterControl,
  SegmentedIconToggle,
} from "../components/ToolbarPrimitives";
import { useSurfaceWidthMode } from "../components/useSurfaceWidthMode";
import { AppDialogFrame } from "../../../components/overlays/AppDialogFrame";
import {
  useStudentsFamiliesDashboardPreviewQuery,
  useStudentsFamiliesDashboardQuery,
  useStudentsFamiliesWarmFamilyRoute,
} from "../objectTree";
import { SupportErrorMessage } from "../../../components/overlays/EngineeringSupportBanner";

type FamilyRecord = {
  id: string;
  familyName: string;
  status: string;
  applicantId: string;
  homePhone: string;
  city: string;
  state: string;
  studentsCount: number;
  studentNames: string[];
};

type StudentRecord = {
  id: string;
  familyId: string;
  firstName: string;
  lastName: string;
  fullName: string;
  grade: string;
  division: string;
  status: string;
  familyName: string;
};

type AddFamilyModalProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: {
    familyName: string;
    homePhone: string;
  }) => Promise<void>;
  loading: boolean;
};

type AddStudentModalProps = {
  open: boolean;
  families: FamilyRecord[];
  loading: boolean;
  onClose: () => void;
  onSubmit: (payload: {
    familyId: string;
    firstName: string;
    lastName: string;
    division: string;
    grade: string;
    status: string;
  }) => Promise<void>;
  onRequestCreateFamily: () => void;
};

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const asArray = (value: unknown): Record<string, unknown>[] =>
  Array.isArray(value) ? (value as Record<string, unknown>[]) : [];

const readText = (value: unknown, fallback = ""): string => {
  const next = String(value ?? "").trim();
  return next || fallback;
};

const toLabel = (value: string): string => {
  const normalized = String(value || "").trim();
  if (!normalized) return "-";
  return normalized
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const GRADE_LABEL_BY_VALUE: Record<string, string> = {
  nursery: "Nursery",
  pre_k: "Pre-K",
  kindergarten: "Kindergarten",
  grade_1: "1",
  grade_2: "2",
  grade_3: "3",
  grade_4: "4",
  grade_5: "5",
  grade_6: "6",
  grade_7: "7",
  grade_8: "8",
};

const GRADE_VALUE_BY_LABEL: Record<string, string> = {
  Nursery: "nursery",
  "Pre-K": "pre_k",
  Kindergarten: "kindergarten",
  "1": "grade_1",
  "2": "grade_2",
  "3": "grade_3",
  "4": "grade_4",
  "5": "grade_5",
  "6": "grade_6",
  "7": "grade_7",
  "8": "grade_8",
};

const DIVISION_LABEL_BY_VALUE: Record<string, string> = {
  boys: "Boys",
  girls: "Girls",
  preschool: "Pre-School",
};

const DIVISION_VALUE_BY_LABEL: Record<string, string> = {
  Boys: "boys",
  Girls: "girls",
  "Pre-School": "preschool",
};

const STUDENT_STATUS_LABEL_BY_VALUE: Record<string, string> = {
  active: "Active",
  inactive: "Inactive",
  withdrawn: "Withdrawn",
  graduated: "Graduated",
  transferred: "Transferred",
};

const STUDENT_STATUS_VALUE_BY_LABEL: Record<string, string> = {
  Active: "active",
  Inactive: "inactive",
  Withdrawn: "withdrawn",
  Graduated: "graduated",
  Transferred: "transferred",
};

const normalizeGradeForDisplay = (value: unknown): string => {
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();
  if (!normalized) return "-";
  return GRADE_LABEL_BY_VALUE[normalized] ?? toLabel(normalized);
};

const normalizeDivisionForDisplay = (value: unknown): string => {
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();
  if (!normalized) return "-";
  return DIVISION_LABEL_BY_VALUE[normalized] ?? toLabel(normalized);
};

const normalizeStudentStatusForDisplay = (value: unknown): string => {
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();
  if (!normalized) return "Active";
  return STUDENT_STATUS_LABEL_BY_VALUE[normalized] ?? toLabel(normalized);
};

const normalizeFamilyStatusForDisplay = (value: unknown): string => {
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();
  if (!normalized) return "Active";
  if (normalized === "archived") return "Archived";
  if (normalized === "inactive") return "Inactive";
  return "Active";
};

const AddFamilyModal = ({
  open,
  onClose,
  onSubmit,
  loading,
}: AddFamilyModalProps) => {
  const [familyName, setFamilyName] = useState("");
  const [homePhone, setHomePhone] = useState("");

  useEffect(() => {
    if (!open) return;
    setFamilyName("");
    setHomePhone("");
  }, [open]);

  if (!open) return null;

  return (
    <AppDialogFrame
      open={open}
      onClose={onClose}
      title="Add New Family"
      description="Create a new family profile for Students & Families CRM."
      testId="students-families-add-family-modal"
      maxWidthClassName="max-w-lg"
      panelClassName="rounded-xl border-slate-200"
      bodyClassName="grid gap-3"
      bodyPaddingClassName="p-5"
      footerPaddingClassName="px-5 py-4"
      footer={
        <>
          <button
            type="button"
            data-testid="students-families-add-family-cancel-button"
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="students-families-add-family-submit-button"
            className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
            onClick={() =>
              onSubmit({
                familyName: familyName.trim(),
                homePhone: homePhone.trim(),
              })
            }
            disabled={!familyName.trim() || loading}
          >
            {loading ? "Saving..." : "Add Family"}
          </button>
        </>
      }
    >
      <div className="grid gap-3">
        <label className="flex flex-col gap-1 text-xs font-semibold text-slate-600">
          Family Name
          <input
            data-testid="students-families-add-family-name-input"
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
            placeholder="e.g. Gertler"
            value={familyName}
            onChange={(event) => setFamilyName(event.target.value)}
          />
        </label>

        <label className="flex flex-col gap-1 text-xs font-semibold text-slate-600">
          Home Phone
          <input
            data-testid="students-families-add-family-phone-input"
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
            placeholder="e.g. 732-555-0101"
            value={homePhone}
            onChange={(event) => setHomePhone(event.target.value)}
          />
        </label>
      </div>
    </AppDialogFrame>
  );
};

const AddStudentModal = ({
  open,
  families,
  loading,
  onClose,
  onSubmit,
  onRequestCreateFamily,
}: AddStudentModalProps) => {
  const [familyId, setFamilyId] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [division, setDivision] = useState("Boys");
  const [grade, setGrade] = useState("5");
  const [status, setStatus] = useState("Active");

  useEffect(() => {
    if (!open) return;
    setFamilyId(families[0]?.id ?? "");
    setFirstName("");
    setLastName("");
    setDivision("Boys");
    setGrade("5");
    setStatus("Active");
  }, [families, open]);

  if (!open) return null;

  return (
    <AppDialogFrame
      open={open}
      onClose={onClose}
      title="Add New Student"
      description="Create a new student record in Students & Families CRM."
      testId="students-families-add-student-modal"
      maxWidthClassName="max-w-xl"
      panelClassName="rounded-xl border-slate-200"
      bodyPaddingClassName="p-5"
      footerPaddingClassName="px-5 py-4"
      footerClassName="justify-between"
      footer={
        <>
          <button
            type="button"
            data-testid="students-families-add-student-create-family-button"
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
            onClick={onRequestCreateFamily}
          >
            + Add Family
          </button>

          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="students-families-add-student-cancel-button"
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="students-families-add-student-submit-button"
              className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
              onClick={() =>
                onSubmit({
                  familyId,
                  firstName: firstName.trim(),
                  lastName: lastName.trim(),
                  division,
                  grade,
                  status,
                })
              }
              disabled={
                !familyId ||
                !firstName.trim() ||
                !lastName.trim() ||
                loading ||
                families.length === 0
              }
            >
              {loading ? "Saving..." : "Add Student"}
            </button>
          </div>
        </>
      }
    >
      {families.length === 0 ? (
        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          Create a family profile before adding a student.
        </div>
      ) : null}

      <div className="mt-4 grid gap-3 md:grid-cols-2">
        <label className="flex flex-col gap-1 text-xs font-semibold text-slate-600 md:col-span-2">
          Family
          <select
            data-testid="students-families-add-student-family-select"
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800"
            value={familyId}
            onChange={(event) => setFamilyId(event.target.value)}
          >
            {families.map((family) => (
              <option key={family.id} value={family.id}>
                {family.familyName}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 text-xs font-semibold text-slate-600">
          First Name
          <input
            data-testid="students-families-add-student-first-name-input"
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
            placeholder="e.g. Jonathan"
            value={firstName}
            onChange={(event) => setFirstName(event.target.value)}
          />
        </label>

        <label className="flex flex-col gap-1 text-xs font-semibold text-slate-600">
          Last Name
          <input
            data-testid="students-families-add-student-last-name-input"
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
            placeholder="e.g. Stone"
            value={lastName}
            onChange={(event) => setLastName(event.target.value)}
          />
        </label>

        <label className="flex flex-col gap-1 text-xs font-semibold text-slate-600">
          Division
          <select
            data-testid="students-families-add-student-division-select"
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
            value={division}
            onChange={(event) => setDivision(event.target.value)}
          >
            {["Boys", "Girls", "Pre-School"].map((option) => (
              <option key={option}>{option}</option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 text-xs font-semibold text-slate-600">
          Grade
          <select
            data-testid="students-families-add-student-grade-select"
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
            value={grade}
            onChange={(event) => setGrade(event.target.value)}
          >
            {[
              "Nursery",
              "Pre-K",
              "Kindergarten",
              "1",
              "2",
              "3",
              "4",
              "5",
              "6",
              "7",
              "8",
            ].map((option) => (
              <option key={option}>{option}</option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 text-xs font-semibold text-slate-600">
          Status
          <select
            data-testid="students-families-add-student-status-select"
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
            value={status}
            onChange={(event) => setStatus(event.target.value)}
          >
            {[
              "Active",
              "Inactive",
              "Withdrawn",
              "Graduated",
              "Transferred",
            ].map((option) => (
              <option key={option}>{option}</option>
            ))}
          </select>
        </label>
      </div>
    </AppDialogFrame>
  );
};

const DEFAULT_FILTERS: FilterValue = {
  grade: "All Grades",
  division: "All Divisions",
  status: "All Statuses",
  siblings: "Show All",
};

const deriveFamilies = (value: unknown): FamilyRecord[] => {
  const items = asArray(asRecord(value).items);
  return items.map((item, index) => {
    const address = asRecord(item.address);
    return {
      id: readText(item.id, `family-${index + 1}`),
      familyName: readText(item.familyName, `Family ${index + 1}`),
      status: normalizeFamilyStatusForDisplay(item.status),
      applicantId: readText(item.applicantId, "-"),
      homePhone: readText(item.homePhone, "-"),
      city: readText(address.city, "-"),
      state: readText(address.state, "-"),
      studentsCount: 0,
      studentNames: [],
    };
  });
};

const deriveStudents = (
  value: unknown,
  familyId: string,
  familyName: string,
): StudentRecord[] => {
  const students = asArray(asRecord(value).students);
  return students.map((item, index) => {
    const firstName = readText(item.firstName, "Student");
    const lastName = readText(item.lastName, `${index + 1}`);
    return {
      id: readText(item.id, `${familyId}-student-${index + 1}`),
      familyId,
      firstName,
      lastName,
      fullName: `${lastName}, ${firstName}`,
      grade: normalizeGradeForDisplay(item.grade),
      division: normalizeDivisionForDisplay(item.division),
      status: normalizeStudentStatusForDisplay(item.status),
      familyName,
    };
  });
};

const DASHBOARD_SKELETON_ROW_COUNT = 6;

const LoadingPulseBlock = ({ className }: { className: string }) => (
  <div className={`animate-pulse rounded-full bg-gray-100 ${className}`} />
);

const StudentsFamiliesToolbarSkeleton = ({
  isStackedLayout,
}: {
  isStackedLayout: boolean;
}) => (
  <>
    <div className="flex min-w-0 grow items-center gap-4 overflow-hidden">
      <section
        data-testid="students-families-toolbar-skeleton"
        className="flex w-full max-w-[360px] min-w-0 items-center gap-3 rounded-2xl border border-gray-100 bg-white px-3 py-2 shadow-sm"
      >
        <LoadingPulseBlock className="h-4 w-4 rounded-full bg-gray-200" />
        <LoadingPulseBlock className="h-4 flex-1 rounded-full" />
        <LoadingPulseBlock className="h-8 w-8 rounded-xl bg-[#f4fefc]" />
      </section>

      {!isStackedLayout ? (
        <aside className="flex items-center gap-2 rounded-2xl border border-gray-100 bg-white p-2 shadow-sm">
          <LoadingPulseBlock className="h-8 w-20 rounded-xl" />
          <LoadingPulseBlock className="h-8 w-24 rounded-xl" />
          <LoadingPulseBlock className="h-8 w-24 rounded-xl" />
          <LoadingPulseBlock className="h-8 w-24 rounded-xl" />
        </aside>
      ) : null}
    </div>

    <nav
      className="flex shrink-0 items-center gap-3 self-start lg:self-center"
      aria-label="Students & Families dashboard controls"
    >
      <LoadingPulseBlock className="h-10 w-24 rounded-lg" />
      <LoadingPulseBlock className="h-10 w-28 rounded-lg" />
      <LoadingPulseBlock className="h-10 w-10 rounded-lg bg-primary/10" />
    </nav>
  </>
);

const StudentsTableSkeleton = () => (
  <div className="overflow-x-auto rounded-3xl border border-gray-100 bg-white shadow-card">
    <table className="table-sticky-last-action-col-md-up min-w-full divide-y divide-gray-100 text-sm">
      <thead className="bg-gray-50/30 text-left text-xs font-bold uppercase tracking-widest text-irongrey/40">
        <tr>
          <th className="px-6 py-4">Student</th>
          <th className="px-6 py-4">Grade / Division</th>
          <th className="px-6 py-4">Family</th>
          <th className="px-6 py-4">Status</th>
          <th className="px-6 py-4">Actions</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-50 bg-white">
        {Array.from({ length: DASHBOARD_SKELETON_ROW_COUNT }, (_, index) => (
          <tr
            key={`students-loading-row-${index + 1}`}
            data-testid="students-families-student-loading-row"
            className="animate-pulse"
          >
            <td className="px-6 py-4 align-top">
              <div className="space-y-2">
                <LoadingPulseBlock className="h-4 w-32 rounded-md" />
                <LoadingPulseBlock className="h-3 w-20 rounded-md bg-gray-50" />
              </div>
            </td>
            <td className="px-6 py-4 align-top">
              <div className="space-y-2">
                <LoadingPulseBlock className="h-4 w-16 rounded-md" />
                <LoadingPulseBlock className="h-3 w-24 rounded-md bg-gray-50" />
              </div>
            </td>
            <td className="px-6 py-4 align-top">
              <LoadingPulseBlock className="h-4 w-28 rounded-md" />
            </td>
            <td className="px-6 py-4 align-top">
              <LoadingPulseBlock className="h-6 w-20 rounded-full" />
            </td>
            <td className="px-6 py-4 align-top">
              <LoadingPulseBlock className="h-4 w-20 rounded-md" />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const FamiliesTableSkeleton = () => (
  <div className="overflow-x-auto rounded-3xl border border-gray-100 bg-white shadow-card">
    <table className="min-w-full divide-y divide-gray-100 text-sm">
      <thead className="bg-gray-50/30 text-left text-xs font-bold uppercase tracking-widest text-irongrey/40">
        <tr>
          <th className="px-6 py-4">Family Name</th>
          <th className="px-6 py-4">Primary Contact</th>
          <th className="px-6 py-4">Address</th>
          <th className="px-6 py-4">Students</th>
          <th className="px-6 py-4">Status</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-50 bg-white">
        {Array.from({ length: DASHBOARD_SKELETON_ROW_COUNT }, (_, index) => (
          <tr
            key={`families-loading-row-${index + 1}`}
            data-testid="students-families-family-loading-row"
            className="animate-pulse"
          >
            <td className="px-6 py-4 align-top">
              <div className="space-y-2">
                <LoadingPulseBlock className="h-4 w-32 rounded-md" />
                <LoadingPulseBlock className="h-3 w-20 rounded-md bg-gray-50" />
              </div>
            </td>
            <td className="px-6 py-4 align-top">
              <LoadingPulseBlock className="h-4 w-28 rounded-md" />
            </td>
            <td className="px-6 py-4 align-top">
              <LoadingPulseBlock className="h-4 w-24 rounded-md" />
            </td>
            <td className="px-6 py-4 align-top">
              <LoadingPulseBlock className="h-4 w-36 rounded-md" />
            </td>
            <td className="px-6 py-4 align-top">
              <LoadingPulseBlock className="h-6 w-20 rounded-full" />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const useDashboardNavigate = () => {
  const navigationContext = useContext(NavigationContext);

  return useCallback(
    (to: string) => {
      if (!to) return;

      if (navigationContext?.navigator) {
        navigationContext.navigator.push(to, undefined, {});
        return;
      }

      if (typeof window !== "undefined") {
        window.location.assign(to);
      }
    },
    [navigationContext],
  );
};

const isUnsupportedStudentsFamiliesOperation = (
  error: unknown,
  operationName: "getStudentsDashboardTree",
): boolean => {
  const message = readDomainRouterErrorMessage(
    error,
    error instanceof Error ? error.message : String(error ?? ""),
  );
  return message.includes(`unsupported field ${operationName}`);
};

export const StudentsDashboard = () => {
  const navigate = useDashboardNavigate();
  const warmFamilyRoute = useStudentsFamiliesWarmFamilyRoute();
  const dashboardRootRef = useRef<HTMLDivElement | null>(null);
  const dashboardSurface = useSurfaceWidthMode(dashboardRootRef);
  const isStackedLayout = dashboardSurface.isStacked;
  const [activeView, setActiveView] = useState<"students" | "families">(
    "students",
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<FilterValue>(DEFAULT_FILTERS);
  const [selectedFamilyId, setSelectedFamilyId] = useState<string>("");
  const [addStudentOpen, setAddStudentOpen] = useState(false);
  const [addFamilyOpen, setAddFamilyOpen] = useState(false);
  const [mutationError, setMutationError] = useState<string | null>(null);

  const [createFamilyProfile, createFamilyProfileState] =
    useDomainRouterMutation("createFamilyProfile");
  const [createStudentProfile, createStudentProfileState] =
    useDomainRouterMutation("createStudentProfile");

  const familiesQuery = useStudentsFamiliesDashboardQuery();
  const usesLegacyDashboardFallback = isUnsupportedStudentsFamiliesOperation(
    familiesQuery.error,
    "getStudentsDashboardTree",
  );
  const fallbackFamiliesQuery = useDomainRouterQuery(
    "listFamilies",
    {
      input: JSON.stringify({ limit: 200, offset: 0 }),
    },
    { skip: !usesLegacyDashboardFallback },
  );
  const activeFamiliesQuery = usesLegacyDashboardFallback
    ? fallbackFamiliesQuery
    : familiesQuery;
  const families = useMemo(
    () => deriveFamilies(activeFamiliesQuery.data),
    [activeFamiliesQuery.data],
  );

  useEffect(() => {
    if (!selectedFamilyId && families.length > 0) {
      setSelectedFamilyId(families[0].id);
    }
  }, [families, selectedFamilyId]);

  const selectedFamily =
    families.find((family) => family.id === selectedFamilyId) ??
    families[0] ??
    null;
  const familyDetailQuery = useStudentsFamiliesDashboardPreviewQuery(
    selectedFamily?.id ?? "",
  );
  const fallbackFamilyDetailQuery = useDomainRouterQuery(
    "getFamily",
    { familyId: selectedFamily?.id ?? "" },
    {
      skip: !usesLegacyDashboardFallback || !selectedFamily?.id,
    },
  );
  const activeFamilyDetailQuery = usesLegacyDashboardFallback
    ? fallbackFamilyDetailQuery
    : familyDetailQuery;

  const students = useMemo(() => {
    if (!selectedFamily) return [];
    return deriveStudents(
      activeFamilyDetailQuery.data,
      selectedFamily.id,
      selectedFamily.familyName,
    );
  }, [activeFamilyDetailQuery.data, selectedFamily]);

  const familiesWithCounts = useMemo(() => {
    if (!selectedFamily) return families;
    return families.map((family) =>
      family.id === selectedFamily.id
        ? {
            ...family,
            studentsCount: students.length,
            studentNames: students.map((student) => student.firstName),
          }
        : family,
    );
  }, [families, selectedFamily, students]);

  const filteredStudents = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    return students.filter((student) => {
      if (filters.grade !== "All Grades" && student.grade !== filters.grade)
        return false;
      if (
        filters.division !== "All Divisions" &&
        student.division !== filters.division
      )
        return false;
      if (
        filters.status !== "All Statuses" &&
        student.status !== filters.status
      )
        return false;
      if (!normalizedQuery) return true;
      return (
        student.fullName.toLowerCase().includes(normalizedQuery) ||
        student.familyName.toLowerCase().includes(normalizedQuery) ||
        student.id.toLowerCase().includes(normalizedQuery)
      );
    });
  }, [filters.division, filters.grade, filters.status, searchQuery, students]);

  const filteredFamilies = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    return familiesWithCounts.filter((family) => {
      if (filters.status !== "All Statuses" && family.status !== filters.status)
        return false;
      if (!normalizedQuery) return true;
      return (
        family.familyName.toLowerCase().includes(normalizedQuery) ||
        family.homePhone.toLowerCase().includes(normalizedQuery) ||
        family.applicantId.toLowerCase().includes(normalizedQuery)
      );
    });
  }, [familiesWithCounts, filters.status, searchQuery]);

  const handleCreateFamily = async (payload: {
    familyName: string;
    homePhone: string;
  }) => {
    try {
      setMutationError(null);
      const created = await createFamilyProfile({
        input: JSON.stringify({
          familyName: payload.familyName,
          homePhone: payload.homePhone || null,
          status: "active",
        }),
      });
      const createdFamilyId = String(
        (created as Record<string, unknown>)?.id ?? "",
      ).trim();
      await activeFamiliesQuery.refetch();
      if (createdFamilyId) {
        setSelectedFamilyId(createdFamilyId);
      }
      setActiveView("families");
      setAddFamilyOpen(false);
    } catch (error) {
      setMutationError(
        error instanceof Error
          ? error.message
          : "Failed to create family profile.",
      );
    }
  };

  const handleCreateStudent = async (payload: {
    familyId: string;
    firstName: string;
    lastName: string;
    division: string;
    grade: string;
    status: string;
  }) => {
    try {
      setMutationError(null);
      await createStudentProfile({
        input: JSON.stringify({
          familyId: payload.familyId,
          firstName: payload.firstName,
          lastName: payload.lastName,
          division: DIVISION_VALUE_BY_LABEL[payload.division] ?? "boys",
          grade: GRADE_VALUE_BY_LABEL[payload.grade] ?? "grade_5",
          status: STUDENT_STATUS_VALUE_BY_LABEL[payload.status] ?? "active",
        }),
      });

      setSelectedFamilyId(payload.familyId);
      await Promise.all([
        activeFamiliesQuery.refetch(),
        activeFamilyDetailQuery.refetch(),
      ]);
      setAddStudentOpen(false);
    } catch (error) {
      setMutationError(
        error instanceof Error
          ? error.message
          : "Failed to create student profile.",
      );
    }
  };

  const isMutating =
    createFamilyProfileState.isLoading || createStudentProfileState.isLoading;
  const isInitialLoading =
    activeFamiliesQuery.isLoading ||
    (activeView === "students" &&
      Boolean(selectedFamily) &&
      activeFamilyDetailQuery.isLoading &&
      students.length === 0);

  const toolbarPrimaryRow = (
    <>
      <div className="flex min-w-0 w-full grow items-center gap-4 overflow-hidden">
        <SearchFilterControl
          inputTestId="students-families-search-input"
          toggleTestId="students-families-filter-toggle-button"
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder={
            activeView === "students"
              ? "Search students..."
              : "Search families..."
          }
          toggleActive={!isStackedLayout && showFilters}
          onToggleFilters={() => setShowFilters((previous) => !previous)}
        />

        {!isStackedLayout ? (
          <aside
            aria-hidden={!showFilters}
            className={`flex items-center rounded-2xl border border-gray-100 bg-white p-1 shadow-sm max-w-full transition-all ease-in-out ${
              showFilters
                ? "duration-300 opacity-100 translate-x-0"
                : "duration-150 max-w-0 opacity-0 -translate-x-4 overflow-hidden pointer-events-none"
            }`}
          >
            <StudentsFamiliesFilters value={filters} onChange={setFilters} />
          </aside>
        ) : null}
      </div>

      <nav
        className="flex w-full flex-wrap items-center justify-between gap-2 self-start sm:justify-end xl:w-auto xl:flex-nowrap xl:self-center"
        aria-label="Students & Families dashboard controls"
      >
        <ActionIconButton
          dataTestId="students-families-export-button"
          icon={Download}
          label="Export"
          variant="outline"
          className="gap-1 border-gray-200 text-sm font-semibold"
        />
        <SegmentedIconToggle
          value={activeView}
          onChange={setActiveView}
          options={[
            {
              value: "students",
              icon: UserRoundSearch,
              label: "Students view",
              testId: "students-families-view-students-button",
            },
            {
              value: "families",
              icon: UsersRound,
              label: "Families view",
              testId: "students-families-view-families-button",
            },
          ]}
        />
        <button
          type="button"
          aria-label="Add Student"
          data-testid="students-families-add-student-button"
          className="bg-primary hover:bg-primary-hover text-white p-2 rounded-lg shadow-lg shadow-primary/20 transition-all cursor-pointer"
          onClick={() => setAddStudentOpen(true)}
        >
          <Plus size={24} />
        </button>
      </nav>
    </>
  );

  const loadingPrimaryRow = (
    <StudentsFamiliesToolbarSkeleton isStackedLayout={isStackedLayout} />
  );

  return (
    <div
      ref={dashboardRootRef}
      data-testid="students-families-dashboard-root"
      data-layout-mode={dashboardSurface.mode}
      className="h-full min-w-0"
    >
      <div className="grid min-w-0 gap-4">
        <section
          data-testid={
            isInitialLoading ? "students-families-dashboard-loading" : undefined
          }
          className="min-w-0 space-y-4 rounded-2xl border-none bg-transparent p-0 shadow-none md:p-0"
        >
          <HeaderShell
            mode={dashboardSurface.mode}
            className="border-b border-gray-100 pb-4"
            primaryRow={
              isInitialLoading ? loadingPrimaryRow : toolbarPrimaryRow
            }
            secondaryRow={
              <section className="animate-in slide-in-from-top-2 duration-200">
                <StudentsFamiliesFilters
                  value={filters}
                  onChange={setFilters}
                  mobile
                />
              </section>
            }
            showSecondaryRow={
              !isInitialLoading && isStackedLayout && showFilters
            }
          />

          {isInitialLoading ? (
            activeView === "students" ? (
              <StudentsTableSkeleton />
            ) : (
              <FamiliesTableSkeleton />
            )
          ) : activeView === "students" ? (
            <div className="w-full max-w-full overflow-x-auto rounded-3xl border border-gray-100 bg-white shadow-card">
              <table className="table-sticky-last-action-col-md-up min-w-full divide-y divide-gray-100 text-sm">
                <thead className="bg-gray-50/30 text-left text-xs font-bold uppercase tracking-widest text-irongrey/40">
                  <tr>
                    <th className="px-6 py-4">Student</th>
                    <th className="px-6 py-4">Grade / Division</th>
                    <th className="px-6 py-4">Family</th>
                    <th className="px-6 py-4">Status</th>
                    <th className="px-6 py-4">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 bg-white">
                  {filteredStudents.length === 0 ? (
                    <tr>
                      <td
                        className="px-6 py-6 text-sm text-irongrey/50"
                        colSpan={5}
                      >
                        No students for the selected family.
                      </td>
                    </tr>
                  ) : null}
                  {filteredStudents.map((student) => (
                    <tr
                      key={student.id}
                      data-testid={`students-families-student-row-${student.id}`}
                      className="cursor-pointer transition-colors hover:bg-gray-50/50"
                      onClick={() => {
                        if (!usesLegacyDashboardFallback) {
                          void warmFamilyRoute(student.familyId);
                        }
                        navigate(`/school/students/family/${student.familyId}/children`);
                      }}
                    >
                      <td className="px-6 py-4 align-top">
                        <p className="font-semibold text-irongrey">
                          {student.fullName}
                        </p>
                        <p className="text-xs text-irongrey/40">
                          #{student.id}
                        </p>
                      </td>
                      <td className="px-6 py-4 align-top">
                        <p className="font-semibold text-irongrey">
                          {student.grade}
                        </p>
                        <p className="text-xs text-irongrey/40">
                          {student.division}
                        </p>
                      </td>
                      <td className="px-6 py-4 align-top text-irongrey/80">
                        {student.familyName}
                      </td>
                      <td className="px-6 py-4 align-top">
                        <StudentsFamiliesStatusBadge status={student.status} />
                      </td>
                      <td className="px-6 py-4 align-top text-xs font-semibold text-primary">
                        View Family
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="w-full max-w-full overflow-x-auto rounded-3xl border border-gray-100 bg-white shadow-card">
              <table className="min-w-full divide-y divide-gray-100 text-sm">
                <thead className="bg-gray-50/30 text-left text-xs font-bold uppercase tracking-widest text-irongrey/40">
                  <tr>
                    <th className="px-6 py-4">Family Name</th>
                    <th className="px-6 py-4">Primary Contact</th>
                    <th className="px-6 py-4">Address</th>
                    <th className="px-6 py-4">Students</th>
                    <th className="px-6 py-4">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 bg-white">
                  {filteredFamilies.length === 0 ? (
                    <tr>
                      <td
                        className="px-6 py-6 text-sm text-irongrey/50"
                        colSpan={5}
                      >
                        No family records match the current filters.
                      </td>
                    </tr>
                  ) : null}
                  {filteredFamilies.map((family) => (
                    <tr
                      key={family.id}
                      data-testid={`students-families-family-row-${family.id}`}
                      className="cursor-pointer transition-colors hover:bg-gray-50/50"
                      onClick={() => {
                        if (!usesLegacyDashboardFallback) {
                          void warmFamilyRoute(family.id);
                        }
                        navigate(`/school/students/family/${family.id}/children`);
                      }}
                    >
                      <td className="px-6 py-4 align-top">
                        <p className="font-semibold text-irongrey">
                          {family.familyName}
                        </p>
                        <p className="text-xs text-irongrey/40">
                          ID: {family.id}
                        </p>
                      </td>
                      <td className="px-6 py-4 align-top text-irongrey/80">
                        {family.homePhone}
                      </td>
                      <td className="px-6 py-4 align-top text-irongrey/80">
                        {family.city}, {family.state}
                      </td>
                      <td className="px-6 py-4 align-top text-irongrey/80">
                        {family.studentsCount || 0} Students
                        {family.studentNames.length > 0
                          ? ` / ${family.studentNames.join(", ")}`
                          : ""}
                      </td>
                      <td className="px-6 py-4 align-top">
                        <StudentsFamiliesStatusBadge status={family.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeFamiliesQuery.error ? (
            <SupportErrorMessage
              data-testid="students-families-domain-router-error-banner"
            >
              Failed to load families from domain router.
            </SupportErrorMessage>
          ) : null}

          {mutationError ? (
            <SupportErrorMessage
              data-testid="students-families-dashboard-mutation-error"
            >
              {mutationError}
            </SupportErrorMessage>
          ) : null}
        </section>
      </div>

      <AddFamilyModal
        open={addFamilyOpen}
        loading={createFamilyProfileState.isLoading}
        onClose={() => {
          if (!isMutating) {
            setAddFamilyOpen(false);
          }
        }}
        onSubmit={handleCreateFamily}
      />

      <AddStudentModal
        open={addStudentOpen}
        families={families}
        loading={createStudentProfileState.isLoading}
        onClose={() => {
          if (!isMutating) {
            setAddStudentOpen(false);
          }
        }}
        onSubmit={handleCreateStudent}
        onRequestCreateFamily={() => {
          setAddFamilyOpen(true);
          setAddStudentOpen(false);
        }}
      />
    </div>
  );
};
